import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Search, ExternalLink, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '../lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BsddLookupWidget — Sprint 5
 *
 * Free-form classification-code lookup against the public buildingSMART
 * Data Dictionary. Used by BIM coordinators to verify that
 * `element.classification` values in their IFC files are real published
 * codes — not project-invented strings.
 *
 * The backend keeps the bSDD response cached in-process so repeated
 * lookups of the same code stay cheap.
 */
export default function BsddLookupWidget() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);   // { found, code, name, uri, domain, definition, error? }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    const q = code.trim();
    if (!q) {
      setError('Enter a classification code (e.g. Pr_60_60_29, IfcWall).');
      setResult(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await apiFetch(
        `${API_URL}/api/standards/bsdd/lookup?code=${encodeURIComponent(q)}`
      );
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(`Lookup failed (${resp.status}): ${t.slice(0, 160)}`);
      }
      const json = await resp.json();
      setResult(json);
    } catch (e) {
      setError(e.message || 'Lookup failed');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="bsdd-lookup-widget">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5 text-accent" /> buildingSMART Data Dictionary (bSDD) Lookup
        </CardTitle>
        <CardDescription>
          Verify that a classification code (Uniclass, IFC entity, OmniClass, etc.) is a real
          published reference. Backed by the public bSDD API — results are cached server-side.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex gap-2 mb-3">
          <Input
            placeholder="e.g. Pr_60_60_29  or  IfcWall"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            data-testid="bsdd-lookup-input"
            className="flex-1"
          />
          <Button type="submit" disabled={loading} data-testid="bsdd-lookup-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            Look up
          </Button>
        </form>

        {error && (
          <Alert variant="destructive" data-testid="bsdd-lookup-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {result && (
          <div
            data-testid={result.found ? 'bsdd-lookup-result-found' : 'bsdd-lookup-result-notfound'}
            className={`p-4 rounded border ${
              result.found
                ? 'bg-[hsl(var(--accent))]/5 border-[hsl(var(--accent))]/30'
                : 'bg-muted/40 border-border'
            }`}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-sm font-medium flex items-center gap-2">
                {result.found ? (
                  <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-[hsl(var(--warning))]" />
                )}
                {result.name || result.code}
              </p>
              {result.domain && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{result.domain}</Badge>
              )}
            </div>
            {result.found ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Code: <span className="font-mono text-foreground">{result.code}</span>
                </p>
                {result.definition && (
                  <p className="text-xs text-foreground mt-2 leading-snug">{result.definition}</p>
                )}
                {result.uri && (
                  <a
                    href={result.uri}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 mt-2 text-xs text-accent hover:underline"
                    data-testid="bsdd-lookup-uri"
                  >
                    <ExternalLink className="h-3 w-3" /> View on buildingSMART
                  </a>
                )}
              </>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                No published classification matched <span className="font-mono">{result.code}</span> in
                IFC 4.3 or Uniclass 2015.
                {result.error && (
                  <span className="block mt-1 text-[hsl(var(--warning))]">
                    Network note: {result.error}
                  </span>
                )}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
